import { useEffect, useMemo, useRef, useState } from 'react'
import {
  PART_FIELDS,
  isPartField,
  type PartFieldId,
  type SplitPartGroup,
} from '@/utils/splitPartGroups'
import { TokenFieldBadge } from '@/components/timetable-import/TokenFieldBadge'

const FIELD_CHIP: Record<PartFieldId, string> = {
  class_name: 'bg-orange-100 text-orange-800 border-orange-300',
  subject: 'bg-green-100 text-green-800 border-green-300',
  room: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  ignore: 'bg-gray-200 text-gray-700 border-gray-400',
}

function partKey(group: SplitPartGroup, index: number): string {
  const part = group.parts[index]
  return part?.id || `${group.parent || 'rest'}:${index}:${part?.token || ''}`
}

function groupsKey(groups: SplitPartGroup[]): string {
  return groups
    .map((group) => {
      const parent = group.parent || 'rest'
      const parts = group.parts.map((part, index) => `${partKey(group, index)}:${part.assignable ? 'a' : 'b'}:${part.field || ''}`).join(',')
      return `${parent}=${parts}`
    })
    .join('|')
}

export function SplitPartAssignPanel({
  groups,
  onApply,
  onCancel,
  onSplit,
  canSplit = false,
  requireComplete = false,
}: {
  groups: SplitPartGroup[]
  onApply: (assignments: Array<{ token: string; field: PartFieldId; id?: string }>) => void
  onCancel: () => void
  onSplit?: () => void
  canSplit?: boolean
  requireComplete?: boolean
}) {
  const key = groupsKey(groups)
  const groupsRef = useRef(groups)
  groupsRef.current = groups
  const [draft, setDraft] = useState<Record<string, PartFieldId | null>>({})

  useEffect(() => {
    const next: Record<string, PartFieldId | null> = {}
    for (const group of groupsRef.current) {
      group.parts.forEach((part, index) => {
        if (!part.assignable) return
        next[partKey(group, index)] = isPartField(part.field) ? part.field : null
      })
    }
    setDraft(next)
  }, [key])

  const ready = useMemo(
    () => Object.entries(draft).filter(([, field]) => field) as Array<[string, PartFieldId]>,
    [draft],
  )
  const assignableCount = groups.reduce((sum, group) => sum + group.parts.filter((part) => part.assignable).length, 0)
  const complete = assignableCount > 0 && ready.length === assignableCount

  return (
    <div className="flex flex-col min-h-0 flex-1">
      <p className="mx-2 mt-2 mb-1 px-2 py-1.5 rounded-md text-[11px] bg-blue-50 text-blue-700">
        Pro Teil ein Feld. Ignorieren ist eine eigene Kategorie.
      </p>
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {groups.map((group) => (
          <div key={group.parent || '__rest'} className="border border-gray-200 bg-white rounded-lg">
            <div className="px-2 py-1.5 text-[11px] font-semibold text-gray-500 bg-gray-50 border-b border-gray-200">
              {group.parent ? (
                <>
                  aus <code className="font-mono text-[11px] bg-gray-100 px-1 rounded">{group.parent}</code>
                </>
              ) : (
                'Rest'
              )}
            </div>
            {group.parts.map((part, index) => {
              const key = partKey(group, index)
              const selected = part.assignable ? draft[key] ?? null : null
              const ignored = selected === 'ignore'
              return (
                <div
                  key={key}
                  className={`px-2 py-1.5 border-b border-gray-100 last:border-b-0 ${part.nested ? 'ml-2 border-l-2 border-blue-200 bg-slate-50' : ''} ${ignored ? 'opacity-80' : ''}`}
                >
                  <div className="mb-1">
                    <code className={`block w-full whitespace-normal break-all font-mono text-xs font-bold ${ignored ? 'line-through text-gray-400' : 'text-gray-900'}`} title={part.token}>
                      {part.token}
                    </code>
                    {part.hint || (!part.assignable && part.field) ? (
                      <div className="mt-0.5 flex flex-wrap items-center gap-1">
                        {part.hint ? <span className="text-[10px] text-gray-400">{part.hint}</span> : null}
                        {!part.assignable && part.field ? <TokenFieldBadge field={part.field} /> : null}
                      </div>
                    ) : null}
                  </div>
                  {part.assignable ? (
                    <div className="flex flex-wrap gap-1">
                      {PART_FIELDS.map((field) => {
                        const on = selected === field.id
                        return (
                          <button
                            key={field.id}
                            type="button"
                            onClick={() => setDraft((prev) => ({ ...prev, [key]: field.id }))}
                            className={`text-[10px] font-bold tracking-wide px-1.5 py-0.5 rounded border ${
                              on ? FIELD_CHIP[field.id] : 'bg-white text-gray-500 border-gray-300'
                            }`}
                          >
                            {field.label}
                          </button>
                        )
                      })}
                    </div>
                  ) : null}
                </div>
              )
            })}
          </div>
        ))}
      </div>
      <div className="p-2 border-t border-gray-200 flex flex-col gap-1">
        {onSplit ? (
          <button
            type="button"
            disabled={!canSplit}
            onClick={onSplit}
            className="w-full text-xs px-2 py-1.5 rounded bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 shadow-sm disabled:opacity-40"
          >
            Teilen
          </button>
        ) : null}
        <button
          type="button"
          disabled={requireComplete ? !complete : ready.length === 0}
          onClick={() => onApply(ready.map(([key, field]) => {
            const part = groups.flatMap((group) => group.parts.map((item, index) => ({ item, key: partKey(group, index) }))).find((row) => row.key === key)?.item
            const row: { token: string; field: PartFieldId; id?: string } = { token: part?.token || key, field }
            if (part?.id) row.id = part.id
            return row
          }))}
          className="w-full text-xs px-2 py-1.5 rounded bg-blue-600 text-white border border-blue-600 font-semibold disabled:opacity-40"
        >
          {ready.length === 1 ? '1 Zuordnung übernehmen' : `${ready.length} Zuordnungen übernehmen`}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="w-full text-xs px-2 py-1.5 rounded bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 shadow-sm"
        >
          Abbrechen
        </button>
      </div>
    </div>
  )
}
